// src/features/delivery-boys/types.ts

export enum DeliveryBoyStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  ON_DUTY = 'on_duty',
  OFF_DUTY = 'off_duty',
  SUSPENDED = 'suspended'
}

export enum VehicleType {
  BIKE = 'bike',
  SCOOTER = 'scooter',
  BICYCLE = 'bicycle',
  CAR = 'car',
  WALKING = 'walking'
}

export enum ShiftType {
  MORNING = 'morning',
  EVENING = 'evening',
  NIGHT = 'night',
  FULL_TIME = 'full_time',
  PART_TIME = 'part_time'
}

export enum DeliveryArea {
  NORTH = 'north',
  SOUTH = 'south',
  EAST = 'east',
  WEST = 'west',
  CENTRAL = 'central',
  ALL_AREAS = 'all_areas'
}

export interface DeliveryBoyAddress {
  id: number;
  street: string;
  city: string;
  state: string;
  pincode: string;
  country: string;
  isDefault: boolean;
}

export interface DeliveryBoyDocument {
  id: number;
  type: 'aadhar' | 'pan' | 'driving_license' | 'voter_id' | 'passport';
  documentNumber: string;
  documentUrl?: string;
  isVerified: boolean;
  uploadedAt: string;
}

export interface DeliveryBoyStats {
  totalDeliveries: number;
  completedDeliveries: number;
  cancelledDeliveries: number;
  averageRating: number;
  totalEarnings: number;
  monthlyEarnings: number;
  onTimeDeliveryRate: number;
}

export interface DeliveryBoy {
  id: number;
  employeeId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth: string;
  gender: 'male' | 'female' | 'other';
  profileImage?: string;
  status: DeliveryBoyStatus;
  joiningDate: string;
  lastActiveDate?: string;
  
  // Work Information
  vehicleType: VehicleType;
  vehicleNumber?: string;
  drivingLicenseNumber?: string;
  shiftType: ShiftType;
  deliveryArea: DeliveryArea;
  isAvailable: boolean;
  
  // Contact Information
  address: DeliveryBoyAddress[];
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  
  // Documents
  documents: DeliveryBoyDocument[];
  
  // Performance
  stats: DeliveryBoyStats;
  
  // Financial
  salaryType: 'fixed' | 'commission' | 'hybrid';
  baseSalary?: number;
  commissionRate?: number;
  accountNumber?: string;
  ifscCode?: string;
  bankName?: string;
  
  // Additional
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface DeliveryBoyFilters {
  status?: DeliveryBoyStatus;
  vehicleType?: VehicleType;
  shiftType?: ShiftType;
  deliveryArea?: DeliveryArea;
  isAvailable?: boolean;
  search?: string;
}

export type DeliveryBoyFormData = Omit<DeliveryBoy, 'id' | 'employeeId' | 'stats' | 'createdAt' | 'updatedAt'> & {
  employeeId?: string;
  stats?: Partial<DeliveryBoyStats>;
  createdAt?: string;
  updatedAt?: string;
};

export interface DeliveryBoyFormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  dateOfBirth?: string;
  vehicleType?: string;
  shiftType?: string;
  deliveryArea?: string;
  address?: string;
  salaryType?: string;
  baseSalary?: string;
  commissionRate?: string;
  general?: string;
}

// Helper functions for status colors and labels
export const getStatusColor = (status: DeliveryBoyStatus): string => {
  switch (status) {
    case DeliveryBoyStatus.ACTIVE:
    case DeliveryBoyStatus.ON_DUTY:
      return 'bg-green-100 text-green-800';
    case DeliveryBoyStatus.OFF_DUTY:
      return 'bg-blue-100 text-blue-800';
    case DeliveryBoyStatus.INACTIVE:
      return 'bg-slate-100 text-slate-800';
    case DeliveryBoyStatus.SUSPENDED:
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-slate-100 text-slate-800';
  }
};

export const getVehicleTypeColor = (vehicleType: VehicleType): string => {
  switch (vehicleType) {
    case VehicleType.BIKE:
    case VehicleType.SCOOTER:
      return 'bg-blue-100 text-blue-800';
    case VehicleType.BICYCLE:
    case VehicleType.WALKING:
      return 'bg-green-100 text-green-800';
    case VehicleType.CAR:
      return 'bg-purple-100 text-purple-800';
    default:
      return 'bg-slate-100 text-slate-800';
  }
};

export const getShiftTypeColor = (shiftType: ShiftType): string => {
  switch (shiftType) {
    case ShiftType.MORNING:
      return 'bg-yellow-100 text-yellow-800';
    case ShiftType.EVENING:
      return 'bg-orange-100 text-orange-800';
    case ShiftType.NIGHT:
      return 'bg-indigo-100 text-indigo-800';
    case ShiftType.FULL_TIME:
      return 'bg-green-100 text-green-800';
    case ShiftType.PART_TIME:
      return 'bg-blue-100 text-blue-800';
    default:
      return 'bg-slate-100 text-slate-800';
  }
};

export const formatSalaryDisplay = (salaryType: string, baseSalary?: number, commissionRate?: number): string => {
  if (salaryType === 'fixed' && baseSalary) {
    return `₹${baseSalary.toLocaleString()}/month`;
  } else if (salaryType === 'commission' && commissionRate) {
    return `${commissionRate}% commission`;
  } else if (salaryType === 'hybrid' && baseSalary && commissionRate) {
    return `₹${baseSalary.toLocaleString()} + ${commissionRate}%`;
  }
  return 'Not set';
};

export const generateEmployeeId = (): string => {
  const prefix = 'DB';
  const timestamp = Date.now().toString().slice(-6);
  const random = Math.random().toString(36).substring(2, 4).toUpperCase();
  return `${prefix}${timestamp}${random}`;
};